// packages/web/src/components/account/account-settings-panel.tsx
/**
 * AccountSettingsPanel — profile + notification preferences.
 *
 * 19BN Phase D D4 + D1: the settings tab of AccountDrawer. Three groups:
 *   1. Profile: display name + avatar URL (PATCH /auth/profile)
 *   2. Notify channels: email + push toggles (PATCH /account/preferences)
 *   3. Low-balance threshold: slider 20–500 (PATCH /account/preferences)
 *
 * Pilot scope (D4): avatar is a URL input, not R2 upload — tracked as
 * DEBT-19BN-AVATAR-UPLOAD for 19BO+.
 *
 * Visual: reuses the auth-field CSS classes from auth-modal for field rhythm;
 * toggles/threshold use the inline-style pattern already established by
 * recharge-flow / invite-panel (no new CSS file).
 *
 * Spec: docs/architecture/account-system-design.md §1.2, §4.3, §9.6
 */

import { useEffect, useState } from 'react';
import { useStore } from '../../lib/store/index.js';
import { profileApi } from '../../api/profile.js';
import { IngestApiError } from '../../lib/api-client.js';
import { useT, useLocale } from '../../i18n/index.js';

const THRESHOLD_MIN = 20;
const THRESHOLD_MAX = 500;
const DISPLAY_NAME_MAX = 50;
const AVATAR_URL_RE = /^https?:\/\/.+/i;

export function AccountSettingsPanel() {
  const t = useT();
  const { locale } = useLocale();
  const currentUser = useStore((s) => s.currentUser);
  const updateProfile = useStore((s) => s.updateProfile);
  const notifyChannels = useStore((s) => s.notifyChannels);
  const updateNotifyChannels = useStore((s) => s.updateNotifyChannels);
  const threshold = useStore((s) => s.lowBalanceThreshold);
  const updateThreshold = useStore((s) => s.updateThreshold);
  const progressCloudSync = useStore((s) => s.progressCloudSync);
  const updateProgressCloudSync = useStore((s) => s.updateProgressCloudSync);
  const lastSavedAt = useStore((s) => s.lastSavedAt);
  const [showSyncFields, setShowSyncFields] = useState(false);

  // Profile fields are local until saved — display name / avatar shouldn't
  // mutate the global currentUser until the server confirms.
  const [displayName, setDisplayName] = useState(currentUser?.displayName ?? '');
  const [avatarUrl, setAvatarUrl] = useState(currentUser?.avatarUrl ?? '');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  // Re-sync local profile fields when currentUser changes (e.g. after another
  // tab updated the profile).
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- re-sync local profile fields from currentUser when it changes (e.g. after another tab saved); local edits commit on save so no merge needed
    setDisplayName(currentUser?.displayName ?? '');
    setAvatarUrl(currentUser?.avatarUrl ?? '');
  }, [currentUser?.displayName, currentUser?.avatarUrl]);

  async function saveProfile(e: React.FormEvent) {
    e.preventDefault();
    if (profileSaving) return;
    setProfileMsg(null);

    // Client-side validation mirrors the backend (auth-profile.ts). The backend
    // is the source of truth; this just avoids a round-trip for obvious errors.
    if (displayName.length === 0 || displayName.length > DISPLAY_NAME_MAX) {
      setProfileMsg({ kind: 'err', text: `昵称需 1–${DISPLAY_NAME_MAX} 个字符` });
      return;
    }
    const trimmedAvatar = avatarUrl.trim();
    if (trimmedAvatar && !AVATAR_URL_RE.test(trimmedAvatar)) {
      setProfileMsg({ kind: 'err', text: '头像链接需以 http(s):// 开头' });
      return;
    }

    setProfileSaving(true);
    try {
      const patch: { displayName: string; avatarUrl?: string | null } = { displayName };
      // Distinguish "empty (clear)" from "unchanged" — only send avatarUrl
      // when the user actually typed/cleared it.
      if (trimmedAvatar !== (currentUser?.avatarUrl ?? '')) {
        patch.avatarUrl = trimmedAvatar || null;
      }
      const { user } = await profileApi.update(patch);
      updateProfile(user); // sync auth-slice.currentUser
      setProfileMsg({ kind: 'ok', text: '已保存' });
    } catch (err) {
      const text = err instanceof IngestApiError
        ? err.response.message ?? err.response.error
        : err instanceof Error ? err.message : '保存失败';
      setProfileMsg({ kind: 'err', text });
    } finally {
      setProfileSaving(false);
    }
  }

  async function toggleChannel(key: 'email' | 'push') {
    try {
      await updateNotifyChannels({ [key]: !notifyChannels[key] });
    } catch {
      // updateNotifyChannels reverts optimistic state on failure; nothing to do.
    }
  }

  async function commitThreshold(value: number) {
    // Slider commits on release (onChange fires continuously during drag —
    // we only persist on pointer up via onBlur of the range input wrapper).
    try {
      await updateThreshold(value);
    } catch {
      // reverted by updateThreshold
    }
  }

  async function toggleProgressCloudSync() {
    // 19BO+ Line 2 B4.4 step 4: opt-in for learning-progress cloud sync.
    // The hook activation (App-level) reacts to this flag change.
    try {
      await updateProgressCloudSync(!progressCloudSync);
    } catch {
      // reverted by updateProgressCloudSync
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2xl)' }}>
      {/* ── Profile ───────────────────────────────────────────────────── */}
      <form className="auth-body" onSubmit={saveProfile} style={{ padding: 0 }}>
        <div className="auth-field">
          <label className="auth-field-label" htmlFor="acct-settings-name">
            <span>昵称</span>
          </label>
          <input
            id="acct-settings-name"
            className={`auth-field-input${profileMsg?.kind === 'err' ? ' has-error' : ''}`}
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={DISPLAY_NAME_MAX}
          />
        </div>
        <div className="auth-field">
          <label className="auth-field-label" htmlFor="acct-settings-avatar">
            <span>头像链接</span>
          </label>
          <input
            id="acct-settings-avatar"
            className={`auth-field-input${profileMsg?.kind === 'err' ? ' has-error' : ''}`}
            type="url"
            placeholder="https://"
            value={avatarUrl}
            onChange={(e) => setAvatarUrl(e.target.value)}
          />
        </div>
        {profileMsg && (
          <div
            style={{
              fontSize: 'var(--text-sm)',
              color: profileMsg.kind === 'ok' ? 'var(--color-success-deep)' : 'var(--color-error)',
              marginBottom: 'var(--space-sm)',
            }}
          >
            {profileMsg.text}
          </div>
        )}
        <button
          type="submit"
          className={`auth-btn-primary${profileSaving ? ' is-loading' : ''}`}
          disabled={profileSaving}
        >
          保存资料
        </button>
      </form>

      {/* ── Notification channels ────────────────────────────────────── */}
      <div>
        <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: 'var(--space-md)' }}>
          通知偏好
        </div>
        <ToggleRow
          label="消息中心"
          hint="始终开启"
          checked={notifyChannels.inApp}
          disabled
        />
        <ToggleRow
          label="邮件"
          hint="低余额 / 充值 / 邀请提醒"
          checked={notifyChannels.email}
          onChange={() => void toggleChannel('email')}
        />
        <ToggleRow
          label="浏览器推送"
          hint="需授权 PWA（即将上线）"
          checked={notifyChannels.push}
          onChange={() => void toggleChannel('push')}
        />
      </div>

      {/* ── Progress cloud sync (19BO+ Line 2 B4.4 + B5.4 UX) ─────────── */}
      <div>
        <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: 'var(--space-md)' }}>
          {t('sync.sectionTitle')}
        </div>
        <ToggleRow
          label={t('sync.toggleLabel')}
          hint={t('sync.toggleHint')}
          checked={progressCloudSync}
          onChange={() => void toggleProgressCloudSync()}
        />
        {progressCloudSync && lastSavedAt !== null && (
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: 'var(--space-sm)' }}>
            {t('sync.lastSyncedAt', { time: formatSyncTime(lastSavedAt, locale) })}
          </div>
        )}
        {progressCloudSync && (
          <button
            type="button"
            onClick={() => setShowSyncFields((v) => !v)}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              fontSize: 'var(--text-xs)', color: 'var(--color-action)',
              padding: 'var(--space-xs) 0', marginTop: 'var(--space-sm)',
            }}
          >
            {t('sync.fieldsTitle')}
            {showSyncFields ? ' ▾' : ' ▸'}
          </button>
        )}
        {progressCloudSync && showSyncFields && (
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: 'var(--space-sm)', lineHeight: 1.6 }}>
            <div>{t('sync.fieldsCloudDesc')}</div>
            <div style={{ marginTop: 'var(--space-xs)' }}>{t('sync.fieldsLocalDesc')}</div>
          </div>
        )}
      </div>

      {/* ── Low-balance threshold ────────────────────────────────────── */}
      <div>
        <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: 'var(--space-md)' }}>
          低余额提醒阈值
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
          <input
            type="range"
            min={THRESHOLD_MIN}
            max={THRESHOLD_MAX}
            step={10}
            value={threshold}
            onChange={(e) => {
              // Optimistic: update local immediately for responsive slider;
              // commit to server on release.
              useStore.setState({ lowBalanceThreshold: Number(e.target.value) });
            }}
            onMouseUp={(e) => void commitThreshold(Number((e.target as HTMLInputElement).value))}
            onTouchEnd={(e) => void commitThreshold(Number((e.target as HTMLInputElement).value))}
            style={{ flex: 1 }}
          />
          <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--color-action)', minWidth: '3em', textAlign: 'right' }}>
            {threshold}
          </span>
        </div>
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: 'var(--space-sm)' }}>
          余额低于此值时提醒（{THRESHOLD_MIN}–{THRESHOLD_MAX} 点）
        </div>
      </div>
    </div>
  );
}

function ToggleRow(props: {
  label: string;
  hint: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: () => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 'var(--space-md) 0',
        borderBottom: '1px solid var(--color-border)',
      }}
    >
      <div>
        <div style={{ fontSize: 'var(--text-md)', fontWeight: 500, color: 'var(--color-ink)' }}>{props.label}</div>
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{props.hint}</div>
      </div>
      <button
        type="button"
        disabled={props.disabled}
        onClick={props.onChange}
        aria-pressed={props.checked}
        style={{
          width: '40px',
          height: '22px',
          borderRadius: 'var(--radius-pill)',
          border: 'none',
          background: props.checked ? 'var(--color-action)' : 'var(--color-surface-2)',
          cursor: props.disabled ? 'not-allowed' : 'pointer',
          opacity: props.disabled ? 0.6 : 1,
          position: 'relative',
          transition: 'background 160ms ease',
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: '2px',
            left: props.checked ? '20px' : '2px',
            width: '18px',
            height: '18px',
            borderRadius: '50%',
            background: 'var(--color-white)',
            transition: 'left 160ms ease',
          }}
        />
      </button>
    </div>
  );
}

/** Format a ms-epoch timestamp as a short relative string ("2 分钟前" / "2m ago"). */
function formatSyncTime(ms: number, locale: string): string {
  const diff = Date.now() - ms;
  const min = Math.floor(diff / 60_000);
  const zh = locale.startsWith('zh');
  if (min < 1) return zh ? '刚刚' : 'just now';
  if (min < 60) return zh ? `${min} 分钟前` : `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return zh ? `${hr} 小时前` : `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return zh ? `${day} 天前` : `${day}d ago`;
}
